import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container } from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import {
  createSearchPage,
  BaseSearchProps,
  createRequestParams,
  OnDateRangeFilter,
  ResetDateFilter,
} from '@erecruit/modules';
import { History } from 'history';

import { Resources, ListParams } from '@erecruit/types';
import { connect } from 'react-redux';
import { selectors } from '../../state/reducer';
import { defaultVisibleColumns, generateColumns } from './columns';
import { RootState } from '../../../../redux/reducers';
import {
  parseTableFilters,
  MatchColumnType,
} from '../../../MatchSearch/columns';
import { MATCH_STATUS_TYPES_GROUPS } from '../../helpers';

import { PositionRecordMatchCounters } from '../../types';
import { fetchMatchStatus } from '../../state/reducer';

type MatchStatusSearchFilterData = Pick<
  Resources.MatchSearchFilter,
  'matchStatusTypes' | 'positionIds'
>;

type MatchStatusSearchProps = BaseSearchProps<
  Resources.MatchSearch,
  MatchStatusSearchFilterData,
  Resources.MatchSearchFilter
>;

function getRequestParams(
  _: keyof PositionRecordMatchCounters,
  history: History,
): ListParams<Resources.MatchSearchFilter> {
  //extract last 2 params from URL
  const [positionId, matchStatus] = history.location.pathname
    .split('/')
    .slice(-2);
  return createRequestParams(
    {
      positionIds: [Number(positionId)],
      matchStatusTypes:
        MATCH_STATUS_TYPES_GROUPS[
          matchStatus as keyof PositionRecordMatchCounters
        ],
    },
    nameof<Resources.MatchSearch>(o => o.createdOn),
  );
}

const useColumns = (
  searchParams: ListParams<Resources.PositionSearchFilter>,
  setSearchParams: React.Dispatch<
    React.SetStateAction<ListParams<Resources.PositionSearchFilter>>
  >,
) => {
  const filterByDate: OnDateRangeFilter = useCallback(
    (fieldKey, range) => {
      setSearchParams({
        ...searchParams,
        filter: { ...searchParams.filter, [fieldKey]: range },
      });
    },
    [searchParams, setSearchParams],
  );

  const resetDateFilter: ResetDateFilter = useCallback(
    dateFieldKey => {
      if (
        searchParams.filter &&
        !searchParams.filter[
          dateFieldKey as keyof Resources.PositionSearchFilter
        ]
      )
        return;
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [dateFieldKey as keyof Resources.PositionSearchFilter]: omit,
        ...other
      } = searchParams.filter as Resources.PositionSearchFilter;

      setSearchParams({
        ...searchParams,
        filter: { ...other },
      });
    },
    [searchParams, setSearchParams],
  );
  const filter = searchParams.filter;

  return useMemo(
    () => generateColumns(filterByDate, resetDateFilter, filter || {}),
    [filter, filterByDate, resetDateFilter],
  );
};

const MatchStatusSearch = createSearchPage<
  Resources.MatchSearchFilter,
  MatchStatusSearchProps & Intl.InjectedIntlProps,
  MatchColumnType,
  keyof PositionRecordMatchCounters,
  Resources.MatchSearch,
  MatchStatusSearchFilterData
>({
  searchTermPlaceholderText: 'Search Matches',
  baseQueryRoute: 'matchType',
  defaultSearchQuery: 'submittals',
  alwaysVisibleColumns: defaultVisibleColumns,
  rowKey: nameof<Resources.MatchSearch>(o => o.id),
  getDefaultVisibleColumns: () => defaultVisibleColumns,
  useColumns,
  getRequestParams,
  useQueryParams: false,
  parseFilterParams: (filters, columns, filterParams) => {
    const parsedFilters = parseTableFilters(filters, columns);
    return {
      ...parsedFilters,
      matchStatusTypes: filterParams?.matchStatusTypes,
      positionIds: filterParams?.positionIds,
      searchTerm: filterParams && filterParams.searchTerm,
    };
  },
});

export const MatchStatuses = (props: MatchStatusSearchProps) => {
  const history = useHistory();

  return (
    <Container
      stretch
      gutter={4}
      gutterDirection="vertical"
      padding={12}
      position="relative"
    >
      <MatchStatusSearch
        {...props}
        onRowSelect={(record: Resources.MatchSearch) =>
          history.push(`/match/${record.id}`)
        }
      />
    </Container>
  );
};

export default connect(
  (state: RootState) => ({
    data: selectors.selectPositionRecordState(state).matchStatus.data,
    totalCount: selectors.selectPositionRecordState(state).matchStatus.count,
    isLoading: selectors.selectPositionRecordState(state).matchStatus.isLoading,
  }),
  {
    searchEntities: fetchMatchStatus,
  },
)(MatchStatuses);
